/*
 * InSight Analytics Solutions Limited
 * https://fieldpro.onyxinsight.com/
 *
 * Copyright (c) 2021 Onyx InSight Field Pro.
 * This file is licensed to InSight Analytics Solutions Limited
 */
/* NPM Imports Start */
import React from 'react';
/* NPM Imports End */

/* Project Imports Start */
import { withNavigationWatcher } from './contexts/navigation';
import { LoginPage, ResetPasswordPage } from './pages';
/* Project Imports End */

/* CCS & Image imports Start */
import './pages/PopUp/PopUp.scss';
import AIHubLink from './pages/AIHubLink/AIHubLink';
import OAuthCallback from './pages/login/OAuthCallback';
import SsoConfig from './pages/SingleSignOn/SsoConfig';
/* CCS & Image imports End */

const PartsList = React.lazy(() => import('./pages/PartsList/PartsList'));
const FailureMode = React.lazy(() => import('./pages/FailureMode/FailureMode'));
const UserList = React.lazy(() => import('./pages/UserList/UserList'));
const dataTable = React.lazy(() => import('./pages/datatable'));
const issueChart = React.lazy(() => import('./pages/issuechart'));
const issueGallery = React.lazy(() => import('./pages/issuegallery'));
const issuePunchList = React.lazy(() => import('./pages/issuepunchlist'));
const Configuration = React.lazy(() =>
  import('./pages/Configuration/configuration'),
);
const DisplayData = React.lazy(() =>
  import('./pages/display-data/display-data'),
);
const ReportList = React.lazy(() => import('./pages/Report/Report'));
const ReportViewer = React.lazy(() =>
  import('./pages/ReportViewer/ReportViewer'),
);
const WorkPlanner = React.lazy(() => import('./pages/WorkPlanner/WorkPlanner'));
const GalleryPDFViewer = React.lazy(() =>
  import('./pages/GalleryPDFViewer/GalleryPDFViewer'),
);
const Template = React.lazy(() =>
  import('./pages/ReportViewer/Templates/Default/Default'),
);
const SiteEquipment = React.lazy(() =>
  import('./pages/SiteEquipment/siteequipment'),
);
const Customer = React.lazy(() => import('./pages/customer'));
const superAdminUsers = React.lazy(() => import('./pages/SuperAdminUsers'));
const FeatureManagement = React.lazy(() =>
  import('./pages/FeatureManagement/FeatureManagement'),
);
const CheckListEditor = React.lazy(() =>
  import('./pages/checklist-editor/CheckListEditor'),
);
const BatchPhotoImport = React.lazy(() =>
  import('./pages/BatchImport/BatchImport'),
);
const FileViewer = React.lazy(() =>
  import('./pages/ReportViewer/SharedComponents/FileViewer'),
);

const ROLES = {
  ADMIN: 'Admin',
  COMPANY_ADMIN: 'Company Admin',
  SUPPORT_ADMIN: 'Support Admin',
  SUPER_ADMIN: 'Super Admin',
  MANAGER: 'Manager',
  USER: 'User',
} as const;

type Role = typeof ROLES[keyof typeof ROLES];

interface Route {
  path: string;
  component: React.ComponentType<any>;
  roles: Array<Role>;
}

const privateRoutesArray: Array<Route> = [
  {
    path: '/display-data',
    component: DisplayData,
    roles: [],
  },
  {
    path: '/IssuepunchList',
    component: issuePunchList,
    roles: [],
  },
  {
    path: '/IssueGallery',
    component: issueGallery,
    roles: [],
  },
  {
    path: '/IssueChart',
    component: issueChart,
    roles: [],
  },
  {
    path: '/DataTable',
    component: dataTable,
    roles: [],
  },
  {
    path: '/UserList',
    component: UserList,
    roles: [ROLES.ADMIN, ROLES.COMPANY_ADMIN, ROLES.SUPPORT_ADMIN],
  },
  {
    path: '/FailureMode',
    component: FailureMode,
    roles: [ROLES.ADMIN, ROLES.COMPANY_ADMIN, ROLES.SUPPORT_ADMIN],
  },
  {
    path: '/PartsList',
    component: PartsList,
    roles: [],
  },
  {
    path: '/customerList',
    component: Customer,
    roles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/SuperAdmin',
    component: superAdminUsers,
    roles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/Features',
    component: FeatureManagement,
    roles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/reportList',
    component: ReportList,
    roles: [],
  },
  {
    path: '/WorkPlanner',
    component: WorkPlanner,
    roles: [
      ROLES.ADMIN,
      ROLES.COMPANY_ADMIN,
      ROLES.SUPPORT_ADMIN,
      ROLES.MANAGER,
    ],
  },
  {
    path: '/reportViewerPage',
    component: ReportViewer,
    roles: [],
  },
  {
    path: '/reportViewer/:ID',
    component: Template,
    roles: [],
  },
  {
    path: '/galleryPDFViewer/:COMPANY/:DATA',
    component: GalleryPDFViewer,
    roles: [],
  },
  {
    path: '/SiteEquipment',
    component: SiteEquipment,
    roles: [],
  },
  {
    path: '/ChecklistEditor',
    component: CheckListEditor,
    roles: [
      ROLES.ADMIN,
      ROLES.COMPANY_ADMIN,
      ROLES.SUPPORT_ADMIN,
      ROLES.MANAGER,
    ],
  },
  {
    path: '/BatchPhotoImport',
    component: BatchPhotoImport,
    roles: [],
  },
  {
    path: '/Configuration',
    component: Configuration,
    roles: [ROLES.ADMIN, ROLES.COMPANY_ADMIN, ROLES.SUPPORT_ADMIN],
  },
  {
    path: '/FileViewer/:id',
    component: FileViewer,
    roles: [],
  },
  {
    path: '/FileViewer/:id/:bool',
    component: FileViewer,
    roles: [],
  },
  {
    path: '/FileViewer/:id/:page/:bool',
    component: FileViewer,
    roles: [],
  },
  {
    path: '/AIHubLink',
    component: AIHubLink,
    roles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/SingleSignOnConfiguration',
    component: SsoConfig,
    roles: [ROLES.SUPER_ADMIN],
  },
];

const publicRoutesArray = [
  {
    path: '/login',
    component: LoginPage,
    roles: [],
  },
  {
    path: '/oauth-callback',
    component: OAuthCallback,
    roles: [],
  },
  {
    path: '/set-password/:userid',
    component: ResetPasswordPage,
    roles: [],
  },
  {
    path: '/FileViewer/:id',
    component: FileViewer,
    roles: [],
  },
];

export const privateRoutes = privateRoutesArray.map(route => ({
  ...route,
  component: withNavigationWatcher(route.component),
}));

export const publicRoutes = publicRoutesArray.map(route => ({
  ...route,
  component: withNavigationWatcher(route.component),
}));
